/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * EmailNotifications
 *
 */

import React, { memo, useState, useEffect, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import {
  Toggle,
  Button,
  ConfirmModal,
  Conditional,
  Popup,
  Space,
  Text,
} from '@cashfree-intl/coherent';
import { usePermissionCode } from '@cashfree-intl/auth';

import WithReadPermissions from 'components/WithReadPermission';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { setActiveNavigation } from 'utils/common';
import {
  MERCHANT_NOTIFICATIONS,
  CUSTOMER_NOTIFICATIONS,
  COMMUNICATION_PREFERENCES,
  NOTIFICATIONS,
  NAVIGATION_BAR_OPTIONS,
  NOT_APPLICABLE,
  SUCCESS_RATE_ALERTS,
  SELF_SERVE_SR_ALERTS,
  NEW_COMMS_PREFERENCE,
} from 'utils/constants';
import CommunicationPreferences from 'containers/CommunicationPreferences';
import SuccessRateAlerts from 'containers/SuccessRateAlerts';
import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';

import { MerchantContext } from 'contexts/MerchantContext';
import { MerchantDetailsContext } from 'contexts/MerchantDetailsContext';
import { StyledLinkButton } from 'containers/CommunicationPreferences/styled';
import { ICON_NAME } from 'components/CustomIcons/types';
import PaginatedTable from 'components/PaginatedTable';
import { PERM_CODES } from 'constants/permCodes';
import { formatColumns } from 'components/PaginatedTable/helpers';

import makeSelectEmailNotifications from './selectors';
import reducer from './reducer';
import saga from './saga';
import {
  setActiveTabNavigation,
  getAllEmailsAction,
  updateAllEmailsDataAction,
  enableLoaderAction,
  removeEmailAction,
  updateEmailChannelStatusAction,
  addBulkEmailAction,
  updateCustomerNotificationsStatusAction,
  resetDataAction,
  getCustomerRefundNotificationsAction,
  updateCustomerRefundNotificationsStatusAction,
} from './actions';

import {
  EMAIL_LIST,
  IMPORT_EMAIL_LIST,
  CUSTOMER_NOTIFICATIONS_DATA,
  HEADINGS,
  EMAIL,
  SMS,
  REFUND_EMAILS,
  UNVERIFIED,
  VERIFIED,
  EMAIL_LIST_CATEGORY_SHORTCODE,
} from './constants';

import Loader from '../../components/Loader';
import NavigationBar from '../../components/NavigationBar';
import CustomIcon from '../../components/CustomIcons';
import AddReceipient from './components/AddEmailReceipient';
import Accordion from '../../components/Accordion';

import { resetTwoFactorAuthentication as resetTwoFactorAuthenticationAction } from '../Toast/actions';
import { twoFactorAuthenticationSelector } from '../Toast/selectors';
import { StyledAlert } from './styled';
import { useFetchMerchantRouteConfigs } from '../InsightsAI/hooks';

export function EmailNotifications(props) {
  useInjectReducer({ key: 'emailNotifications', reducer });
  useInjectSaga({ key: 'emailNotifications', saga });

  const { featureToggles = {}, isMerchantImportEnabled } = useContext(
    MerchantContext,
  );
  const { merchantDetails } = useContext(MerchantDetailsContext);
  const { id: merchantId, merchantName, merchantEmail } = merchantDetails;
  const {
    loading: fetchingMerchantRoutes,
    merchantRouteConfigs = [],
  } = useFetchMerchantRouteConfigs();

  const {
    isModuleFederated = false,
    actionsDisabled = false,
    getAllEmails,
    getCustomerRefundNotifications,
    enableLoader,
    updateAllEmailsData,
    removeEmail,
    updateEmailChannelStatus,
    addBulkEmail,
    resetData,
    twoFactorAuthentication,
    updateCustomerNotificationsStatus,
    activeNavigation = MERCHANT_NOTIFICATIONS,
    updateCustomerRefundNotificationsStatus,
    resetTwoFactorAuthentication,
    emailNotifications: {
      isLoading,
      receipientAdded,
      customerEmailNotifications,
      customerSmsNotifications,
      tabs,
      errorInAddingRecipient,
      deletedRefundEmails,
    },
  } = props;

  const hasPermission = isModuleFederated
    ? true
    : // eslint-disable-next-line react-hooks/rules-of-hooks, indent
      usePermissionCode(PERM_CODES.PG_ACCESSNOTIFICATIONS_VIEW);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const env = searchParams.get('env');

  const allNavOptions = NAVIGATION_BAR_OPTIONS[NOTIFICATIONS];
  const { data } = [COMMUNICATION_PREFERENCES, SUCCESS_RATE_ALERTS].includes(
    activeNavigation,
  )
    ? { data: [] }
    : tabs[activeNavigation];

  const isAddEnabledForMerchantNotification =
    activeNavigation === MERCHANT_NOTIFICATIONS &&
    data?.some?.(emailData => emailData.emailLeftCount > 0);

  const [activeIndex, resetActiveIndex] = useState(-1);
  const [emailToBeRemoved, resetEmailToBeRemoved] = useState({});
  const [openModal, resetModalStatus] = useState(false);

  /**
   * Few of the tabs/cards have been moved to new comms preference page in commonwebapp. Hence, when
   * this toggle is enabled, hide those options.
   */
  const isNewCommsPreferencePageEnabled = !!featureToggles[
    NEW_COMMS_PREFERENCE
  ];

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    resetActiveIndex(newIndex);
  };

  const handleCloseIconClick = obj => () => resetEmailToBeRemoved(obj);

  const fetchAllEmails = () => {
    enableLoader();
    getAllEmails({ activeNavigation, isMerchantImportEnabled });
  };

  const fetchAllSms = () => {
    if (activeNavigation === CUSTOMER_NOTIFICATIONS) {
      enableLoader();
      getCustomerRefundNotifications();
    }
  };

  const flowwiseRoutes = useMemo(() => {
    if (fetchingMerchantRoutes) return [];
    return merchantRouteConfigs
      .map(m => ({
        merchantRouteId: m.merchantRouteId,
        name: m.name,
        credId: m.pgCredId,
      }))
      .filter(
        obj => obj.name !== '' && obj.credId !== null && obj.credId !== 0,
      );
  }, [fetchingMerchantRoutes]);

  useEffect(() => {
    if (activeNavigation !== COMMUNICATION_PREFERENCES) {
      fetchAllEmails();
      fetchAllSms();
    }
  }, [activeNavigation, env]);

  useEffect(() => {
    if (twoFactorAuthentication) {
      fetchAllEmails();
      fetchAllSms();
      resetTwoFactorAuthentication(false);
    }
    return () => resetData();
  }, [twoFactorAuthentication]);

  const handleToggleClick = ({ enabled, id, ...rest }) => e => {
    e.stopPropagation();
    enableLoader();
    const title =
      typeof rest.title === 'function' ? rest.displayText : rest.title;
    const name = title
      .toLowerCase()
      .split(' ')
      .join('_');

    Analytics.track(
      `merchants_${name}_${
        EVENTS.SETTINGS.EMAIL_NOTIFICATION.MERCHANT_NOTIFICATION.TOGGLE_CLICK
      }`,
    );
    updateEmailChannelStatus({
      data: { enabled: !enabled, type: id },
      activeNavigation,
    });
    Analytics.trackEventWithNewNomenclature({
      eventName: 'Table_Row_Toggle',
      eventProperties: {
        row_id: id,
      },
    });
  };

  const handleCustomerNotificationToggle = (
    type,
    enabled,
    channel = EMAIL,
    title,
  ) => e => {
    e.stopPropagation();
    enableLoader();
    const name = title
      .toLowerCase()
      .split(' ')
      .join('_');
    Analytics.track(
      `customer_${name}_${
        EVENTS.SETTINGS.EMAIL_NOTIFICATION.CUSTOMER_NOTIFICATION.TOGGLE_CLICK
      }`,
    );
    if (type === 'notifyPayment') {
      const notificationsData = { ...customerEmailNotifications };
      notificationsData[type] = !enabled;
      updateCustomerNotificationsStatus(notificationsData);
    } else {
      updateCustomerRefundNotificationsStatus({
        notificationType: type,
        notificationChannel: channel,
        createdBy: merchantId,
        updatedBy: merchantId,
        isActive: !enabled,
      });
    }
    Analytics.trackEventWithNewNomenclature({
      eventName: 'Table_Row_Toggle',
      eventProperties: {
        row_id: type,
      },
    });
  };

  const handleCancel = () => resetEmailToBeRemoved({});

  const handleConfirm = () => {
    resetEmailToBeRemoved({});
    enableLoader();

    const obj = { data: { ...emailToBeRemoved }, activeNavigation };

    removeEmail(obj);
  };

  const handleNavigationClick = (e, { name }) => {
    setActiveNavigation(name, navigate, location, navigationOptions);
    setActiveTabNavigation(name);
  };

  const handleAddReceipientClick = () => {
    Analytics.track(
      EVENTS.SETTINGS.EMAIL_NOTIFICATION.MERCHANT_NOTIFICATION.ADD_RECIPIENT,
    );
    resetModalStatus(true);
  };

  const closeModal = () => resetModalStatus(false);

  const handleAddReceipientSubmit = (values, actions) => {
    Analytics.track(
      EVENTS.SETTINGS.EMAIL_NOTIFICATION.MERCHANT_NOTIFICATION.RECIPIENT_SUBMIT,
    );
    const { email, ...rest } = values;
    const emailTypes = [];

    Object.keys(rest).forEach(key => {
      if (rest[key]) emailTypes.push(key);
    });
    const isDeletedRefundEmail = deletedRefundEmails.find(
      obj => obj.email === email,
    );
    let updateEmail = null;
    if (isDeletedRefundEmail) {
      const refundEmail = emailTypes.indexOf(REFUND_EMAILS);
      if (refundEmail !== -1) {
        emailTypes.splice(refundEmail, 1);
        updateEmail = {
          email,
          emailType: REFUND_EMAILS,
          isActive: false,
          status: UNVERIFIED,
        };
      }
    }

    addBulkEmail({
      addEmail: { emails: [email], emailTypes },
      updateEmail,
      actions,
    });
  };

  const closeModalAndRefresh = () => {
    resetModalStatus(false);
    fetchAllEmails();
  };

  const getChipState = (enabled, isActive) => {
    let state = '';
    let color = '';
    if (!enabled) {
      state = 'disabled';
      color = 'var(--SECONDARY_LIGHTER)';
    } else if (isActive && enabled) {
      state = 'active';
      color = 'white';
    }
    return { state, color };
  };

  useEffect(() => {
    if (!isLoading) {
      const formattedData = [];
      switch (activeNavigation) {
        case MERCHANT_NOTIFICATIONS: {
          if (data.length > 0) {
            const isAnyEmailAdded = data.some(
              emailDataByType => emailDataByType.emails?.length > 0,
            );
            const emailList = isMerchantImportEnabled
              ? IMPORT_EMAIL_LIST
              : EMAIL_LIST;
            data.forEach(obj => {
              const { id, emails, enabled } = obj;
              const list = emailList[id];
              if (list) {
                const currentEmailCount = emails?.length || 0;
                const showChevronDown =
                  isAnyEmailAdded && currentEmailCount > 0;
                let emailLeftCount = obj.maxEmails - currentEmailCount;
                emailLeftCount = emailLeftCount < 0 ? 0 : emailLeftCount;

                formattedData.push({
                  id,
                  key: id,
                  displayText: list.title,
                  maxEmails: obj.maxEmails,
                  emailLeftCount,
                  emails,
                  enabled,
                  // eslint-disable-next-line no-shadow
                  title: ({ index, activeIndex }) => (
                    <div className="email-title-wrapper">
                      <div>
                        <div className="email-title medium-text text-dark w-600">
                          {list.title}
                        </div>
                        <div className="medium-text text-light">
                          <Conditional
                            if={
                              isNewCommsPreferencePageEnabled &&
                              id === EMAIL_LIST_CATEGORY_SHORTCODE.pgSrUp
                            }
                          >
                            This feature has been moved to new Communications
                            Page.{' '}
                            <StyledLinkButton
                              as="a"
                              link
                              href={`${
                                process.env.COMMON_BASE_PATH
                              }/profile/communication-preference?tabGroup=Updates+and+Alerts`}
                            >
                              Click here to access new page
                            </StyledLinkButton>
                          </Conditional>
                          <Conditional
                            if={
                              !isNewCommsPreferencePageEnabled ||
                              (isNewCommsPreferencePageEnabled &&
                                id !== EMAIL_LIST_CATEGORY_SHORTCODE.pgSrUp)
                            }
                          >
                            {list.description}
                          </Conditional>
                        </div>
                      </div>
                      <Conditional
                        if={
                          !isNewCommsPreferencePageEnabled ||
                          (isNewCommsPreferencePageEnabled &&
                            id !== EMAIL_LIST_CATEGORY_SHORTCODE.pgSrUp)
                        }
                      >
                        <Space gap={3}>
                          <div
                            className={`toggle${
                              showChevronDown || !isAnyEmailAdded
                                ? ''
                                : ' mr-right-40'
                            }`}
                          >
                            <Toggle
                              active={enabled}
                              onToggle={handleToggleClick(obj)}
                              {...{
                                disabled: isModuleFederated || actionsDisabled,
                              }}
                              data-event-name="Table_Row_Toggle"
                              data-ignore-inner-text="true"
                              data-event-properties={JSON.stringify({
                                row_id: id,
                              })}
                            />
                          </div>
                          <Conditional if={showChevronDown}>
                            <span className="align-center">
                              <CustomIcon
                                componentName={ICON_NAME.CHEVRONDOWN}
                                direction={
                                  index === activeIndex ? 'up' : 'down'
                                }
                              />
                            </span>
                          </Conditional>
                        </Space>
                      </Conditional>
                    </div>
                  ),
                  content: () => (
                    <Conditional
                      if={
                        !isNewCommsPreferencePageEnabled ||
                        (isNewCommsPreferencePageEnabled &&
                          id !== EMAIL_LIST_CATEGORY_SHORTCODE.pgSrUp)
                      }
                    >
                      <div className="email-content-wrapper">
                        {emails &&
                          emails.map(({ email, isActive, status }) => {
                            let active = isActive;
                            if (id === REFUND_EMAILS) {
                              switch (status) {
                                case VERIFIED: {
                                  active = 1;
                                  break;
                                }
                                case UNVERIFIED: {
                                  active = 0;
                                  break;
                                }
                                default:
                                  return null;
                              }
                            }
                            const chipState = getChipState(enabled, active);

                            return (
                              <Popup
                                position="top center"
                                content={
                                  <Text variant="b12" className="font-normal">
                                    {active
                                      ? 'Recipient Approved'
                                      : 'Recipient Approval Pending'}
                                  </Text>
                                }
                                trigger={
                                  <div
                                    className={`email ${chipState.state}`}
                                    key={email}
                                  >
                                    {email}
                                    {/* eslint-disable */}
                                    {enabled &&
                                      !isModuleFederated &&
                                      !actionsDisabled && (
                                        <span
                                          className="close pointer"
                                          onClick={handleCloseIconClick({
                                            email,
                                            type: obj.id,
                                          })}
                                          data-event-name="Table_Row"
                                          data-ignore-inner-text="true"
                                          data-event-properties={JSON.stringify(
                                            {
                                              row_id: obj.id,
                                            },
                                          )}
                                        >
                                          <CustomIcon
                                            componentName="CLOSE_NORMAL"
                                            fill={chipState.color}
                                          />
                                        </span>
                                      )}
                                  </div>
                                }
                              />
                            );
                          })}
                      </div>
                    </Conditional>
                  ),
                });
              }
            });
            if (formattedData.length > 0)
              updateAllEmailsData({ data: formattedData, activeNavigation });
          }
          break;
        }
        case CUSTOMER_NOTIFICATIONS: {
          CUSTOMER_NOTIFICATIONS_DATA.forEach(obj => {
            const { key, title, description } = obj;
            const enabledEmailNotificiations = Object.prototype.hasOwnProperty.call(
              customerEmailNotifications,
              key,
            )
              ? customerEmailNotifications[key]
              : NOT_APPLICABLE;

            const enabledSmsNotifications = Object.prototype.hasOwnProperty.call(
              customerSmsNotifications,
              key,
            )
              ? customerSmsNotifications[key]
              : NOT_APPLICABLE;

            formattedData.push({
              id: key,
              key,
              type: () => (
                <div className="email-title-wrapper">
                  <div>
                    <div className="email-title medium-text text-dark w-600">
                      {title}
                    </div>
                    <div className="medium-text text-light">{description}</div>
                  </div>
                </div>
              ),
              displayText: title,

              email: () =>
                enabledEmailNotificiations === NOT_APPLICABLE ? (
                  <Text color="bodyLight">Not Applicable</Text>
                ) : (
                  <div className="toggle">
                    <Toggle
                      active={enabledEmailNotificiations}
                      onToggle={handleCustomerNotificationToggle(
                        key,
                        enabledEmailNotificiations,
                        EMAIL,
                        title,
                      )}
                      {...{
                        disabled: isModuleFederated || actionsDisabled,
                      }}
                      data-event-name="Table_Row_Toggle"
                      data-ignore-inner-text="true"
                      data-event-properties={JSON.stringify({
                        row_id: key,
                      })}
                    />
                  </div>
                ),
              sms: () =>
                enabledSmsNotifications === NOT_APPLICABLE ? (
                  <Text color="bodyLight">Not Applicable</Text>
                ) : (
                  <div className="toggle">
                    <Toggle
                      active={enabledSmsNotifications}
                      onToggle={handleCustomerNotificationToggle(
                        key,
                        enabledSmsNotifications,
                        SMS,
                        title,
                      )}
                      data-event-name="Table_Row_Toggle"
                      data-ignore-inner-text="true"
                      data-event-properties={JSON.stringify({
                        row_id: key,
                      })}
                    />
                  </div>
                ),
            });
          });
          updateAllEmailsData({ data: formattedData, activeNavigation });
          break;
        }
        default:
          break;
      }
    }
  }, [isLoading, customerEmailNotifications, customerSmsNotifications]);

  const navigateToNewCommsPreferencePage = () => {
    window.location.href = `${
      process.env.COMMON_BASE_PATH
    }/profile/communication-preference?tabGroup=Updates+and+Alerts`;
  };

  const getNavigationOptions = () => {
    let navOptions = allNavOptions;
    if (!featureToggles[SELF_SERVE_SR_ALERTS]) {
      navOptions = navOptions.filter(
        option => option.text !== SUCCESS_RATE_ALERTS,
      );
    }

    return navOptions;
  };

  const navigationOptions = getNavigationOptions();

  const formattedColumnId = formatColumns(HEADINGS);
  if (!hasPermission) {
    return (
      <WithReadPermissions
        description="to access notifications page"
        merchantEmail={merchantEmail}
      />
    );
  }

  return (
    <div className="email-notifications-container">
      <Loader active={isLoading} />
      <Conditional if={isNewCommsPreferencePageEnabled}>
        <StyledAlert className="mb-2" type="info" bordered>
          <Space justifyContent="space-between" alignItems="center">
            <div>
              Notification Categories including{' '}
              <span>
                API Updates, Error Updates, SDK Updates and Service Alerts
              </span>{' '}
              have been moved to new Communication Preference Page.{' '}
            </div>
            <Button
              variant="primary"
              className="ml-3"
              onClick={navigateToNewCommsPreferencePage}
            >
              Visit New Page
            </Button>
          </Space>
        </StyledAlert>
      </Conditional>
      <>
        <NavigationBar
          navigationOptions={navigationOptions.map(({ text }) => text)}
          activeNavigation={activeNavigation}
          handleNavigationClick={handleNavigationClick}
        />
        {activeNavigation === MERCHANT_NOTIFICATIONS && (
          <div className="mt-3">
            <div className="description-container">
              <Conditional if={!isModuleFederated && !actionsDisabled}>
                <div className="text-light">
                  Configure the email category that you want to get notified
                  about. Click Add Recipient to add members in your organization
                  to receive the emails.
                </div>
              </Conditional>
              <Conditional if={!isModuleFederated && !actionsDisabled}>
                <Space gap={1}>
                  <Button
                    primary
                    onClick={handleAddReceipientClick}
                    disabled={
                      !isLoading && !isAddEnabledForMerchantNotification
                    }
                    data-event-name="Primary_Button_AddRecipient"
                  >
                    Add Recipient
                  </Button>
                </Space>
              </Conditional>
              <Conditional if={!isModuleFederated && !actionsDisabled}>
                <Conditional if={!isAddEnabledForMerchantNotification}>
                  <Popup
                    content={
                      <Text variant="b12" className="font-normal">
                        Maximum recipients added. Delete a few existing
                        recipients to add new ones.
                      </Text>
                    }
                    trigger={
                      <span className="pointer align-center">
                        <CustomIcon componentName="INFO" />
                      </span>
                    }
                    position="top right"
                  />
                </Conditional>
              </Conditional>
            </div>
            <Accordion
              list={data || []}
              activeIndex={activeIndex}
              handleClick={handleClick}
            />
          </div>
        )}
        <Conditional if={activeNavigation === CUSTOMER_NOTIFICATIONS}>
          <PaginatedTable
            data={data}
            tableHeading={formattedColumnId}
            isPaginated={false}
            fetching={isLoading}
          />
        </Conditional>
        <Conditional if={activeNavigation === COMMUNICATION_PREFERENCES}>
          <CommunicationPreferences
            isCommsPrefEnabled={isNewCommsPreferencePageEnabled}
          />
        </Conditional>

        <Conditional if={activeNavigation === SUCCESS_RATE_ALERTS}>
          <div className="mt-5">
            <SuccessRateAlerts
              merchantId={merchantId}
              merchantName={merchantName}
              product="PG"
              eventType="SRAlert"
              merchantRoutes={flowwiseRoutes}
            />
          </div>
        </Conditional>

        <AddReceipient
          open={openModal}
          closeModal={closeModal}
          handleFormSubmit={handleAddReceipientSubmit}
          closeModalAndRefresh={closeModalAndRefresh}
          receipientAdded={receipientAdded}
          data={data || []}
          errorInAddingRecipient={errorInAddingRecipient}
        />
        {Object.keys(emailToBeRemoved).length > 0 && (
          <ConfirmModal
            danger
            title="Remove Email"
            onClose={handleCancel}
            onConfirm={handleConfirm}
            confirmBtnEvent="Danger_Button_Remove_Email_Confirm"
            closeBtnEvent="Link_Button_Remove_Email_Cancel"
            closeCrossEvent="Remove_Email_Icon_Close"
          >
            {data[activeIndex].displayText} will be disabled on{' '}
            {emailToBeRemoved.email}. Are you sure you want to continue?
          </ConfirmModal>
        )}
      </>
    </div>
  );
}

EmailNotifications.propTypes = {
  isModuleFederated: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  getAllEmails: PropTypes.func.isRequired,
  getCustomerRefundNotifications: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  updateAllEmailsData: PropTypes.func.isRequired,
  removeEmail: PropTypes.func.isRequired,
  updateEmailChannelStatus: PropTypes.func.isRequired,
  addBulkEmail: PropTypes.func.isRequired,
  emailNotifications: PropTypes.object.isRequired,
  twoFactorAuthentication: PropTypes.bool.isRequired,
  resetTwoFactorAuthentication: PropTypes.func.isRequired,
  updateCustomerNotificationsStatus: PropTypes.func.isRequired,
  updateCustomerRefundNotificationsStatus: PropTypes.func.isRequired,
  activeNavigation: PropTypes.string,
  resetData: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  emailNotifications: makeSelectEmailNotifications(),
  twoFactorAuthentication: twoFactorAuthenticationSelector(),
});

function mapDispatchToProps(dispatch) {
  return {
    getAllEmails: data => dispatch(getAllEmailsAction(data)),
    getCustomerRefundNotifications: () =>
      dispatch(getCustomerRefundNotificationsAction()),
    updateAllEmailsData: data => dispatch(updateAllEmailsDataAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    removeEmail: data => dispatch(removeEmailAction(data)),
    updateEmailChannelStatus: data =>
      dispatch(updateEmailChannelStatusAction(data)),
    addBulkEmail: data => dispatch(addBulkEmailAction(data)),
    resetData: () => dispatch(resetDataAction()),
    resetTwoFactorAuthentication: data =>
      dispatch(resetTwoFactorAuthenticationAction(data)),
    updateCustomerNotificationsStatus: data =>
      dispatch(updateCustomerNotificationsStatusAction(data)),
    updateCustomerRefundNotificationsStatus: data =>
      dispatch(updateCustomerRefundNotificationsStatusAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(EmailNotifications);
